import { CONTINENTS } from './countries'

/**
 * The advertising region as the two-pane picker holds it, and its round trip to the
 * `ProtectionInfoDTO.region` array of ISO-2 codes.
 *
 * Custom Rules picks countries by NAME; protection stores CODES. Both walk the same
 * `CONTINENTS` list, so only this mapping differs.
 */
export interface RegionSelection {
	/** Advertise everywhere — the geo signal has nothing to bite on. */
	worldwide: boolean
	/** Picked ISO-2 codes keyed by continent id. Empty whenever `worldwide`. */
	countries: Record<string, string[]>
	/**
	 * Stored codes `CONTINENTS` does not carry — `config/countries.ts` has 251 entries
	 * and the picker places ~196, so HK, MO, PR, GI, GL, RE, GP, MF and ~47 others are
	 * legitimately storable today. Kept out of the panes but written straight back, or
	 * opening the page would silently drop them.
	 */
	unplaced: string[]
}

/** The product's "everywhere" sentinel, and a real entry in `config/countries.ts`. */
export const GLOBAL_CODE = 'GLOBAL'

export const WORLDWIDE: RegionSelection = {
	worldwide: true,
	countries: {},
	unplaced: []
}

/**
 * No region at all — a reachable state, not a bug. Unticking Worldwide with nothing
 * else picked blocks Save with an error rather than snapping back (DEV-2422 §1.2).
 */
export const NO_REGION: RegionSelection = {
	worldwide: false,
	countries: {},
	unplaced: []
}

const CONTINENT_OF = new Map<string, string>()
CONTINENTS.forEach((continent) => {
	continent.countries.forEach((country) =>
		CONTINENT_OF.set(country.id, continent.id)
	)
})

/** Codes the picker can place, in `CONTINENTS` order. */
export const isPlaceableCode = (code: string): boolean => CONTINENT_OF.has(code)

export const regionFromCodes = (
	codes: Array<string> | null | undefined
): RegionSelection => {
	if (!codes || codes.length === 0) return NO_REGION
	// Legacy rows can hold GLOBAL alongside countries — the old control was a plain
	// multi-select over a list that includes it. Any GLOBAL means worldwide.
	if (codes.includes(GLOBAL_CODE)) return WORLDWIDE

	const countries: Record<string, string[]> = {}
	const unplaced: string[] = []
	codes.forEach((code) => {
		const continentId = CONTINENT_OF.get(code)
		if (!continentId) {
			if (!unplaced.includes(code)) unplaced.push(code)
			return
		}
		const bucket = countries[continentId] ?? (countries[continentId] = [])
		if (!bucket.includes(code)) bucket.push(code)
	})
	return { worldwide: false, countries, unplaced }
}

export const regionToCodes = (region: RegionSelection): string[] => {
	if (region.worldwide) return [GLOBAL_CODE]
	const codes: string[] = []
	CONTINENTS.forEach((continent) => {
		const picked = region.countries[continent.id]
		if (!picked?.length) return
		continent.countries.forEach((country) => {
			if (picked.includes(country.id)) codes.push(country.id)
		})
	})
	return codes.concat(region.unplaced)
}

/** What the trigger counts — unplaced codes are real selections and count too. */
export const regionCountryCount = (region: RegionSelection): number =>
	Object.values(region.countries).reduce((sum, ids) => sum + ids.length, 0) +
	region.unplaced.length

export const isRegionEmpty = (region: RegionSelection): boolean =>
	!region.worldwide && regionCountryCount(region) === 0
